import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import { Box } from '@mui/material';

import { Card, LoadingOverlay } from '@occc-app/design-system';
import { OfferFragment } from '@occc/graphql';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';
import { validateConfiguration } from '../../validation';
import { ConfigureFormValues } from '../../types';

type ConfigureFormProps = {
  onSubmit: (values: ConfigureFormValues, form: FormApi<ConfigureFormValues>) => void;
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  initialValues: Partial<ConfigureFormValues>;
  loading: boolean;
  currentFlowType: Flow_Types;
  isFormEditable: boolean;
  onNextStepButtonClick?: () => void;
};

export type Offer = Pick<OfferFragment, 'id' | 'name' | 'engagementType'>;

export const ConfigureForm = ({
  onSubmit,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  isFormEditable,
  onNextStepButtonClick,
}: ConfigureFormProps) => {
  const renderForm: FormRenderProps<ConfigureFormValues>['render'] = useCallback(
    ({ handleSubmit, submitting, values, dirty }: FormRenderProps<ConfigureFormValues>) => {
      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={() => onCancelClick(dirty)}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={() => onCancelClick(dirty)}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
            <Card sx={{ py: 2, mb: 0 }}>
              <Box mx={3} my={2}>
                <OfferNameAndDescriptionConfiguration entityName='Billboard' />
                <OfferFulfillmentPriorityField mt={2} />
              </Box>
              <CustomData values={values.customData} />
            </Card>
          </OfferConfigurationWizardFormLayout>
        </OfferForm>
      );
    },
    [isFormEditable, currentFlowType, onNextStepButtonClick, onCancelClick],
  );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isFormEditable}>
        <Form<ConfigureFormValues>
          onSubmit={onSubmit}
          render={renderForm}
          initialValues={initialValues}
          validate={validateConfiguration}
        />
      </CanEditFormFields>
      {loading && <LoadingOverlay rounded />}
    </Box>
  );
};
